import React, { useCallback } from "react";

import styles from "./style.module.scss";
import { LocalisedText } from "../../i18n";
import { AddressFieldsState } from "../../types";
import NewAddressFields from "../NewAddressFields";
import AddressOptionItem from "../AddressOptionItem";
import CustomAddressOption from "../CustomAddressOption";
import { CustomerAddress } from "../../generated/graphql";

interface ShippingAddressFormProps {
  addressState: AddressFieldsState;
  setAddress: (a: AddressFieldsState) => void;
  handleRemoveAddress: (a: CustomerAddress) => void;
  addressList: CustomerAddress[];
  isDisabled?: boolean;
}

const ShippingAddressForm: React.FC<ShippingAddressFormProps> = (
  props: ShippingAddressFormProps
) => {
  const {
    addressState,
    setAddress,
    isDisabled,
    addressList,
    handleRemoveAddress,
  } = props;

  const setCountry = useCallback(
    (value: string) => {
      setAddress({
        ...addressState,
        country: value,
      });
    },
    [addressState, setAddress]
  );

  const setAddress1 = useCallback(
    (value: string) => {
      setAddress({
        ...addressState,
        address1: value,
      });
    },
    [addressState, setAddress]
  );

  const setAddress2 = useCallback(
    (value: string) => {
      setAddress({
        ...addressState,
        address2: value,
      });
    },
    [addressState, setAddress]
  );

  const setSaveForFutureUse = useCallback(
    (value: boolean) => {
      setAddress({
        ...addressState,
        saveForFutureUse: value,
      });
    },
    [addressState, setAddress]
  );

  const onAddressSelect = useCallback(
    (index: number) => {
      setAddress({
        type: "reuse",
        id: addressList[index].id,
        country: addressList[index].country || "",
        address1: addressList[index].address1 || "",
        address2: addressList[index].address2 || "",
        saveForFutureUse: false,
      });
    },
    [addressList, setAddress]
  );

  const onNewAddressSelected = useCallback(() => {
    if (addressState.type === "new") return;
    setAddress({
      type: "new",
      country: "HKG",
      address1: "",
      address2: "",
      saveForFutureUse: false,
    });
  }, [addressState, setAddress]);

  const onAddressRemove = useCallback(
    (index: number) => {
      const address = addressList[index];
      // Select another address if the selected address is to be removed
      if (addressState.type === "reuse" && addressState.id === address.id) {
        if (addressList.length === 1) {
          onNewAddressSelected();
        } else {
          if (index === 0) {
            console.log("set select 1");
            onAddressSelect(1);
          } else {
            onAddressSelect(index - 1);
          }
        }
      }
      handleRemoveAddress(address);
    },
    [
      addressList,
      handleRemoveAddress,
      onNewAddressSelected,
      onAddressSelect,
      addressState,
    ]
  );

  return (
    <div className={styles.shippingAddress}>
      <div className={styles.title}>
        <LocalisedText id="view.addressForm.shippingAddress" />
      </div>
      {addressList.length === 0 && (
        <NewAddressFields
          country={addressState.country}
          address1={addressState.address1}
          address2={addressState.address2}
          saveForFutureUse={addressState.saveForFutureUse}
          setCountry={setCountry}
          setAddress1={setAddress1}
          setAddress2={setAddress2}
          setSaveForFutureUse={setSaveForFutureUse}
          isDisabled={isDisabled}
          checkboxDescriptionId="view.addressForm.saveForFuture"
        />
      )}
      {addressList.length > 0 && (
        <div className={styles.options}>
          {addressList.map((address, index) => {
            return (
              <AddressOptionItem
                key={address.id}
                country={address.country || ""}
                address1={address.address1 || ""}
                address2={address.address2 || ""}
                index={index}
                isSelected={
                  addressState.type === "reuse" &&
                  addressState.id === address.id
                }
                onSelect={onAddressSelect}
                onRemove={onAddressRemove}
              />
            );
          })}
          <CustomAddressOption
            country={addressState.country}
            address1={addressState.address1}
            address2={addressState.address2}
            saveForFutureUse={addressState.saveForFutureUse}
            setCountry={setCountry}
            setAddress1={setAddress1}
            setAddress2={setAddress2}
            setSaveForFutureUse={setSaveForFutureUse}
            isSelected={addressState.type === "new"}
            onSelect={onNewAddressSelected}
            isDisabled={isDisabled}
            checkboxDescriptionId="view.addressForm.saveForFuture"
            titleId="view.addressForm.enterNewShippingInfo"
          />
        </div>
      )}
    </div>
  );
};

export default ShippingAddressForm;
